// Configuration admin (Phase 0.2a) — the real Settings screen. Leave types and
// holiday calendars are now editable data the leave module consumes. Payroll
// components (0.2b) and approval hierarchies (0.2c) will add sections here.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Drawer, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import { CalendarOff, CalendarDays, Plus, Trash2, Pencil, X, Wallet, Percent, ShieldCheck, PenLine, Timer } from 'lucide-react';
import { Pill, Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, WidgetGrid, KPI } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

// A native <select> (avoids the portaled-MUI-menu misalignment under body zoom).
function Select({ value, onChange, options, style }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ height: 40, borderRadius: 10, border: `1px solid ${AG.n300}`, padding: '0 10px', fontSize: 14, fontFamily: 'inherit', background: '#fff', color: AG.text, width: '100%', ...style }}
    >
      {options.map((o) => (
        <option key={String(o.value)} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

const Field = ({ label, children }) => (
  <label style={{ display: 'block', marginBottom: 12 }}>
    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>{label}</span>
    {children}
  </label>
);

const tabBtn = (active) => ({
  padding: '8px 14px', borderRadius: 10, border: `1px solid ${active ? AG.primary : AG.n200}`,
  background: active ? AG.primaryLightest : '#fff', color: active ? AG.primaryDark : AG.n600,
  fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
});

export default function ConfigAdmin({ showToast }) {
  const [tab, setTab] = useState('leave');
  const [entities, setEntities] = useState([]);

  useEffect(() => {
    api.orgEntities().then(setEntities).catch(() => setEntities([]));
  }, []);

  // Company-wide + each entity, for scope selectors.
  const scopeOptions = [{ value: '', label: 'Company-wide' }, ...entities.map((e) => ({ value: e.id, label: e.name }))];
  const scopeLabel = (entityId, entityName) => (entityId ? entityName || entities.find((e) => e.id === entityId)?.name || 'Entity' : 'Company-wide');

  return (
    <>
      <SectionTitle title="Configuration" sub="Define the rules your HR processes run on — no code changes." />
      <div className="flex items-center gap-2" style={{ margin: '14px 0 20px', flexWrap: 'wrap' }}>
        <button type="button" style={tabBtn(tab === 'leave')} onClick={() => setTab('leave')}>Leave types</button>
        <button type="button" style={tabBtn(tab === 'holidays')} onClick={() => setTab('holidays')}>Holiday calendars</button>
        <button type="button" style={tabBtn(tab === 'payroll')} onClick={() => setTab('payroll')}>Payroll components</button>
        <button type="button" style={tabBtn(tab === 'overtime')} onClick={() => setTab('overtime')}>Overtime</button>
        <button type="button" style={tabBtn(tab === 'tax')} onClick={() => setTab('tax')}>Income tax</button>
        <button type="button" style={tabBtn(tab === 'manpower')} onClick={() => setTab('manpower')}>Manpower</button>
        <button type="button" style={tabBtn(tab === 'fx')} onClick={() => setTab('fx')}>Exchange rates</button>
        <button type="button" style={tabBtn(tab === 'integrations')} onClick={() => setTab('integrations')}>Integrations</button>
        <button type="button" style={tabBtn(tab === 'approvals')} onClick={() => setTab('approvals')}>Approval flows</button>
        <button type="button" style={tabBtn(tab === 'roles')} onClick={() => setTab('roles')}>Roles &amp; access</button>
        <button type="button" style={tabBtn(tab === 'agreements')} onClick={() => setTab('agreements')}>Agreements</button>
        <button type="button" style={tabBtn(tab === 'baseline')} onClick={() => setTab('baseline')}>Baseline</button>
      </div>
      {tab === 'leave' && <LeaveTypes showToast={showToast} scopeOptions={scopeOptions} scopeLabel={scopeLabel} />}
      {tab === 'holidays' && <Holidays showToast={showToast} scopeOptions={scopeOptions} scopeLabel={scopeLabel} />}
      {tab === 'payroll' && <PayrollComponents showToast={showToast} scopeOptions={scopeOptions} scopeLabel={scopeLabel} />}
      {tab === 'overtime' && <OvertimeRules showToast={showToast} scopeOptions={scopeOptions} scopeLabel={scopeLabel} />}
      {tab === 'tax' && <TaxSchemes showToast={showToast} scopeOptions={scopeOptions} scopeLabel={scopeLabel} />}
      {tab === 'manpower' && <Manpower showToast={showToast} scopeOptions={scopeOptions} />}
      {tab === 'fx' && <ExchangeRates showToast={showToast} scopeOptions={scopeOptions} scopeLabel={scopeLabel} />}
      {tab === 'integrations' && <Integrations showToast={showToast} />}
      {tab === 'approvals' && <Workflows showToast={showToast} />}
      {tab === 'roles' && <RolesAccess showToast={showToast} scopeOptions={scopeOptions} scopeLabel={scopeLabel} />}
      {tab === 'agreements' && <Agreements showToast={showToast} />}
      {tab === 'baseline' && <Baseline showToast={showToast} />}
    </>
  );
}

// ── Baseline metrics (current-state HR costs for KPI proof) ──────────────────
const BLANK_METRIC = { activity: '', hoursManual: 0, errorRatePct: '', materialCost: '', notes: '' };
function Baseline({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.baseline().then(setRows).catch((e) => { showToast(e.message); setRows([]); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.activity || busy) return;
    setBusy(true);
    try {
      const payload = { activity: form.activity, hoursManual: form.hoursManual, errorRatePct: form.errorRatePct, materialCost: form.materialCost, notes: form.notes };
      if (form.id) await api.updateBaseline(form.id, payload); else await api.addBaseline(payload);
      showToast(form.id ? 'Baseline updated' : 'Baseline captured');
      setForm(null); await load();
    } catch (e) { showToast(e.message); } finally { setBusy(false); }
  };
  const remove = async (m) => { if (busy) return; setBusy(true); try { await api.deleteBaseline(m.id); await load(); } catch (e) { showToast(e.message); } finally { setBusy(false); } };

  if (!rows) return <CenterSpinner />;
  const totalHours = rows.reduce((s, r) => s + (r.hoursManual || 0), 0);

  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: AG.n500 }}>Current-state HR effort — captured so the programme's %-improvement KPIs are provable.{rows.length ? ` Total ${totalHours} manual hrs/cycle.` : ''}</span>
        <PrimaryBtn onClick={() => setForm({ ...BLANK_METRIC })}><Plus size={14} style={{ marginRight: 6 }} /> Capture activity</PrimaryBtn>
      </div>
      {rows.length === 0 ? (
        <Card><EmptyState icon={Timer} title="No baseline captured" desc="Record manual hours / error rate / material cost per HR activity." /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((m) => (
            <div key={m.id} className="flex items-center justify-between" style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 12, padding: '12px 14px', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{m.activity}</div>
                <div className="flex items-center gap-2" style={{ marginTop: 4, flexWrap: 'wrap' }}>
                  <Pill tone="info">{m.hoursManual} hrs</Pill>
                  {m.errorRatePct != null && <Pill tone="warning">{m.errorRatePct}% error</Pill>}
                  {m.materialCost != null && <Pill tone="neutral">Rs {m.materialCost}</Pill>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <IconButton size="small" onClick={() => setForm({ id: m.id, activity: m.activity, hoursManual: m.hoursManual, errorRatePct: m.errorRatePct ?? '', materialCost: m.materialCost ?? '', notes: m.notes || '' })} aria-label="Edit"><Pencil size={15} /></IconButton>
                <IconButton size="small" onClick={() => remove(m)} aria-label="Delete"><Trash2 size={15} /></IconButton>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit baseline' : 'Capture HR activity baseline'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <Field label="Activity"><TextField fullWidth size="small" value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} placeholder="Payroll processing" /></Field>
              <Field label="Manual hours / cycle"><TextField fullWidth size="small" type="number" value={form.hoursManual} onChange={(e) => setForm({ ...form, hoursManual: e.target.value })} /></Field>
              <Field label="Error rate % (optional)"><TextField fullWidth size="small" type="number" value={form.errorRatePct} onChange={(e) => setForm({ ...form, errorRatePct: e.target.value })} /></Field>
              <Field label="Material cost Rs (optional)"><TextField fullWidth size="small" type="number" value={form.materialCost} onChange={(e) => setForm({ ...form, materialCost: e.target.value })} /></Field>
              <Field label="Notes (optional)"><TextField fullWidth size="small" multiline minRows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={save} disabled={busy}>{form?.id ? 'Save' : 'Capture'}</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Agreements: issue NDAs/consents + e-signature requests ───────────────────
function Agreements({ showToast }) {
  const [consents, setConsents] = useState(null);
  const [sigs, setSigs] = useState([]);
  const [people, setPeople] = useState([]);
  const [issue, setIssue] = useState(null); // { employeeId, kind, title, body }
  const [send, setSend] = useState(null); // { subjectType, title, signerName, signerEmail }
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.consents().then(setConsents).catch((e) => { showToast(e.message); setConsents([]); });
    api.signatureRequests().then(setSigs).catch(() => setSigs([]));
  };
  useEffect(() => {
    load();
    api.employees().then((es) => setPeople(es || [])).catch(() => setPeople([]));
  }, []);

  const doIssue = async () => {
    if (!issue.employeeId || !issue.title || busy) return;
    setBusy(true);
    try { await api.issueConsent({ employeeId: issue.employeeId, kind: issue.kind, title: issue.title, body: issue.body }); showToast('Consent issued'); setIssue(null); load(); }
    catch (e) { showToast(e.message); } finally { setBusy(false); }
  };
  const doSend = async () => {
    if (!send.title || !send.signerEmail || busy) return;
    setBusy(true);
    try { await api.sendSignature(send); showToast('Sent for signature'); setSend(null); load(); }
    catch (e) { showToast(e.message); } finally { setBusy(false); }
  };
  const mark = async (sr, status) => { try { await api.updateSignature(sr.id, status); load(); } catch (e) { showToast(e.message); } };

  if (!consents) return <CenterSpinner />;
  const tone = (s) => (s === 'Signed' ? 'success' : s === 'Declined' || s === 'Cancelled' ? 'error' : 'warning');

  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: AG.n500 }}>Issue NDAs / data consents and track e-signature requests.</span>
        <div className="flex items-center gap-2">
          <OutlineBtn onClick={() => setSend({ subjectType: 'Contract', title: '', signerName: '', signerEmail: '' })}><PenLine size={14} style={{ marginRight: 6 }} /> Send for e-sign</OutlineBtn>
          <PrimaryBtn onClick={() => setIssue({ employeeId: '', kind: 'NDA', title: '', body: '' })}><Plus size={14} style={{ marginRight: 6 }} /> Issue consent</PrimaryBtn>
        </div>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: AG.n400, margin: '4px 0 8px' }}>Consents &amp; NDAs</div>
      {consents.length === 0 ? (
        <Card><EmptyState icon={PenLine} title="No consents issued" desc="Issue an NDA or data consent to an employee." /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {consents.map((c) => (
            <div key={c.id} className="flex items-center justify-between" style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 12, padding: '12px 14px', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{c.title} <span style={{ color: AG.n400, fontWeight: 400 }}>· {c.kind} · {c.employeeName}</span></div>
                {c.signedAt && <div style={{ fontSize: 12, color: AG.n500 }}>Signed by {c.signatureName}</div>}
              </div>
              <Pill tone={tone(c.status)}>{c.status}</Pill>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: AG.n400, margin: '18px 0 8px' }}>E-signature requests</div>
      {sigs.length === 0 ? (
        <span style={{ fontSize: 13, color: AG.n500 }}>No signature requests yet.</span>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {sigs.map((s) => (
            <div key={s.id} className="flex items-center justify-between" style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 12, padding: '12px 14px', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{s.title} <span style={{ color: AG.n400, fontWeight: 400 }}>· {s.subjectType} · {s.signerEmail}</span></div>
                <div style={{ fontSize: 12, color: AG.n500 }}>via {s.provider}</div>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone={tone(s.status)}>{s.status}</Pill>
                {s.status === 'Sent' && <OutlineBtn onClick={() => mark(s, 'Signed')}>Mark signed</OutlineBtn>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!issue} onClose={() => !busy && setIssue(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Issue NDA / consent</DialogTitle>
        <DialogContent>
          {issue && (
            <div style={{ paddingTop: 6 }}>
              <Field label="Employee"><Select value={issue.employeeId} onChange={(v) => setIssue({ ...issue, employeeId: v })} options={[{ value: '', label: 'Select an employee…' }, ...people.map((p) => ({ value: p.id, label: `${p.name} · ${p.dept}` }))]} /></Field>
              <Field label="Kind"><Select value={issue.kind} onChange={(v) => setIssue({ ...issue, kind: v })} options={[{ value: 'NDA', label: 'NDA' }, { value: 'DataConsent', label: 'Data-handling consent' }]} /></Field>
              <Field label="Title"><TextField fullWidth size="small" value={issue.title} onChange={(e) => setIssue({ ...issue, title: e.target.value })} placeholder="Mutual NDA 2026" /></Field>
              <Field label="Text (optional)"><TextField fullWidth size="small" multiline minRows={2} value={issue.body} onChange={(e) => setIssue({ ...issue, body: e.target.value })} /></Field>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setIssue(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={doIssue} disabled={busy}>Issue</PrimaryBtn>
        </DialogActions>
      </Dialog>

      <Dialog open={!!send} onClose={() => !busy && setSend(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Send for e-signature</DialogTitle>
        <DialogContent>
          {send && (
            <div style={{ paddingTop: 6 }}>
              <Field label="Subject"><Select value={send.subjectType} onChange={(v) => setSend({ ...send, subjectType: v })} options={[{ value: 'Contract', label: 'Contract' }, { value: 'Offer', label: 'Offer' }, { value: 'Document', label: 'Document' }, { value: 'NDA', label: 'NDA' }]} /></Field>
              <Field label="Title"><TextField fullWidth size="small" value={send.title} onChange={(e) => setSend({ ...send, title: e.target.value })} placeholder="Employment contract" /></Field>
              <Field label="Signer name"><TextField fullWidth size="small" value={send.signerName} onChange={(e) => setSend({ ...send, signerName: e.target.value })} /></Field>
              <Field label="Signer email"><TextField fullWidth size="small" value={send.signerEmail} onChange={(e) => setSend({ ...send, signerEmail: e.target.value })} placeholder="name@company.com" /></Field>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setSend(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={doSend} disabled={busy}>Send</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Leave types ──────────────────────────────────────────────────────────────
const BLANK_TYPE = { entityId: '', name: '', code: '', defaultDays: 0, accrual: 'Annual', eligibility: 'All', carryForward: false, paid: true };

function LeaveTypes({ showToast, scopeOptions, scopeLabel }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null); // create/edit dialog state
  const [busy, setBusy] = useState(false);

  const load = () => api.leaveTypes().then(setRows).catch((e) => { showToast(e.message); setRows([]); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    const name = (form.name || '').trim();
    const code = (form.code || '').trim();
    if (!name || !code || busy) return;
    setBusy(true);
    try {
      const payload = { name, code, defaultDays: Number(form.defaultDays) || 0, accrual: form.accrual, eligibility: form.eligibility, carryForward: !!form.carryForward, paid: !!form.paid };
      if (form.id) await api.updateLeaveType(form.id, payload);
      else await api.createLeaveType({ ...payload, entityId: form.entityId || null });
      showToast(form.id ? 'Leave type updated' : `Leave type "${name}" created`);
      setForm(null);
      await load();
    } catch (e) { showToast(e.message); } finally { setBusy(false); }
  };

  const toggleActive = async (t) => {
    try { await api.updateLeaveType(t.id, { active: !t.active }); await load(); }
    catch (e) { showToast(e.message); }
  };
  const remove = async (t) => {
    if (busy) return;
    setBusy(true);
    try { await api.deleteLeaveType(t.id); showToast(`Deleted ${t.name}`); await load(); }
    catch (e) { showToast(e.message); } finally { setBusy(false); }
  };

  if (!rows) return <CenterSpinner />;

  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: AG.n500 }}>{rows.length} leave type{rows.length === 1 ? '' : 's'} · drives the apply-leave form, eligibility and new-hire entitlements</span>
        <PrimaryBtn onClick={() => setForm({ ...BLANK_TYPE })}><Plus size={14} style={{ marginRight: 6 }} /> New leave type</PrimaryBtn>
      </div>

      {rows.length === 0 ? (
        <Card><EmptyState icon={CalendarOff} title="No leave types" desc="Add the first leave type employees can apply for." /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {rows.map((t) => (
            <div key={t.id} style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 14, padding: 16, opacity: t.active ? 1 : 0.6 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: AG.text }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{t.code} · {scopeLabel(t.entityId, t.entity?.name)}</div>
                </div>
                <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                  <IconButton size="small" onClick={() => setForm({ id: t.id, name: t.name, code: t.code, defaultDays: t.defaultDays, accrual: t.accrual, eligibility: t.eligibility, carryForward: t.carryForward, paid: t.paid, entityId: t.entityId || '' })} aria-label="Edit"><Pencil size={15} /></IconButton>
                  <IconButton size="small" onClick={() => remove(t)} aria-label="Delete"><Trash2 size={15} /></IconButton>
                </div>
              </div>
              <div className="flex items-center gap-2" style={{ marginTop: 12, flexWrap: 'wrap' }}>
                <Pill tone="info">{t.defaultDays} days</Pill>
                <Pill tone={t.eligibility === 'All' ? 'neutral' : 'primary'}>{t.eligibility}</Pill>
                {t.carryForward && <Pill tone="neutral">Carry-forward</Pill>}
                <Pill tone={t.paid ? 'success' : 'warning'}>{t.paid ? 'Paid' : 'Unpaid'}</Pill>
              </div>
              <div className="flex items-center justify-between" style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${AG.divider}` }}>
                <span style={{ fontSize: 12, color: AG.n500 }}>{t.accrual} accrual</span>
                <button type="button" onClick={() => toggleActive(t)} style={{ fontSize: 12, fontWeight: 600, color: t.active ? AG.successDark : AG.n500, background: 'none', border: 'none', cursor: 'pointer' }}>
                  {t.active ? 'Active' : 'Inactive'} · toggle
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit leave type' : 'New leave type'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <Field label="Name"><TextField fullWidth size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Annual Leave" /></Field>
              <Field label="Code"><TextField fullWidth size="small" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ANNUAL" disabled={!!form.id} /></Field>
              {!form.id && <Field label="Scope"><Select value={form.entityId} onChange={(v) => setForm({ ...form, entityId: v })} options={scopeOptions} /></Field>}
              <Field label="Default entitlement (days)"><TextField fullWidth size="small" type="number" value={form.defaultDays} onChange={(e) => setForm({ ...form, defaultDays: e.target.value })} /></Field>
              <Field label="Accrual"><Select value={form.accrual} onChange={(v) => setForm({ ...form, accrual: v })} options={[{ value: 'Annual', label: 'Annual' }, { value: 'Monthly', label: 'Monthly' }, { value: 'None', label: 'None' }]} /></Field>
              <Field label="Eligibility"><Select value={form.eligibility} onChange={(v) => setForm({ ...form, eligibility: v })} options={[{ value: 'All', label: 'All employees' }, { value: 'Female', label: 'Female only' }, { value: 'Male', label: 'Male only' }]} /></Field>
              <div className="flex items-center gap-4" style={{ marginTop: 4 }}>
                <label className="flex items-center gap-2" style={{ fontSize: 13, color: AG.n700, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.carryForward} onChange={(e) => setForm({ ...form, carryForward: e.target.checked })} style={{ accentColor: AG.primary }} /> Carry-forward
                </label>
                <label className="flex items-center gap-2" style={{ fontSize: 13, color: AG.n700, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.paid} onChange={(e) => setForm({ ...form, paid: e.target.checked })} style={{ accentColor: AG.primary }} /> Paid
                </label>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={save} disabled={busy}>{form?.id ? 'Save' : 'Create'}</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Holiday calendars ────────────────────────────────────────────────────────
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Toggleable weekend-day picker (0=Sun … 6=Sat).
function WeekendPicker({ value, onChange }) {
  const set = new Set(value || []);
  const toggle = (i) => {
    const next = new Set(set);
    next.has(i) ? next.delete(i) : next.add(i);
    onChange([...next].sort((a, b) => a - b));
  };
  return (
    <div className="flex items-center" style={{ gap: 5, flexWrap: 'wrap' }}>
      {DOW_LABELS.map((d, i) => (
        <button key={i} type="button" onClick={() => toggle(i)} style={{ padding: '5px 9px', borderRadius: 8, border: `1px solid ${set.has(i) ? AG.primary : AG.n200}`, background: set.has(i) ? AG.primaryLightest : '#fff', color: set.has(i) ? AG.primary : AG.n500, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{d}</button>
      ))}
    </div>
  );
}

function Holidays({ showToast, scopeOptions, scopeLabel }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null); // new-calendar dialog
  const [manage, setManage] = useState(null); // calendar id being managed
  const [busy, setBusy] = useState(false);

  const load = () => api.holidayCalendars().then(setRows).catch((e) => { showToast(e.message); setRows([]); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = (form.name || '').trim();
    const year = Number(form.year);
    if (!name || !year || busy) return;
    setBusy(true);
    try {
      await api.createHolidayCalendar({ name, year, entityId: form.entityId || null, weekendDays: form.weekendDays });
      showToast(`Calendar "${name}" created`);
      setForm(null);
      await load();
    } catch (e) { showToast(e.message); } finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (busy) return;
    setBusy(true);
    try { await api.deleteHolidayCalendar(c.id); showToast(`Deleted ${c.name}`); await load(); }
    catch (e) { showToast(e.message); } finally { setBusy(false); }
  };

  if (!rows) return <CenterSpinner />;

  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: AG.n500 }}>{rows.length} calendar{rows.length === 1 ? '' : 's'} · leave day-counting excludes these dates + weekends</span>
        <PrimaryBtn onClick={() => setForm({ name: '', year: new Date().getFullYear(), entityId: '', weekendDays: [0, 6] })}><Plus size={14} style={{ marginRight: 6 }} /> New calendar</PrimaryBtn>
      </div>

      {rows.length === 0 ? (
        <Card><EmptyState icon={CalendarDays} title="No holiday calendars" desc="Create a calendar and add public holidays." /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {rows.map((c) => (
            <div key={c.id} style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 14, padding: 16, opacity: c.active ? 1 : 0.6 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: AG.text }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{c.year} · {scopeLabel(c.entityId, c.entityName)}</div>
                </div>
                <IconButton size="small" onClick={() => remove(c)} aria-label="Delete"><Trash2 size={15} /></IconButton>
              </div>
              <div className="flex items-center justify-between" style={{ marginTop: 14 }}>
                <Pill tone="info">{c.holidayCount} holiday{c.holidayCount === 1 ? '' : 's'}</Pill>
                <OutlineBtn onClick={() => setManage(c.id)}>Manage</OutlineBtn>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New holiday calendar</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <Field label="Name"><TextField fullWidth size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pakistan 2026" /></Field>
              <Field label="Year"><TextField fullWidth size="small" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></Field>
              <Field label="Scope"><Select value={form.entityId} onChange={(v) => setForm({ ...form, entityId: v })} options={scopeOptions} /></Field>
              <Field label="Weekend"><WeekendPicker value={form.weekendDays} onChange={(weekendDays) => setForm({ ...form, weekendDays })} /></Field>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={create} disabled={busy}>Create</PrimaryBtn>
        </DialogActions>
      </Dialog>

      <CalendarManager id={manage} onClose={() => { setManage(null); load(); }} showToast={showToast} />
    </>
  );
}

// Drawer to add/remove holidays in one calendar.
function CalendarManager({ id, onClose, showToast }) {
  const [cal, setCal] = useState(null);
  const [add, setAdd] = useState({ date: '', name: '' });
  const [busy, setBusy] = useState(false);

  const load = () => api.holidayCalendar(id).then(setCal).catch(() => setCal(null));
  useEffect(() => { if (id) { setCal(null); load(); } else { setCal(null); setAdd({ date: '', name: '' }); } }, [id]);

  const addHoliday = async () => {
    if (!add.date || !add.name || busy) return;
    setBusy(true);
    try { await api.addHoliday(id, add.date, add.name.trim()); setAdd({ date: '', name: '' }); await load(); }
    catch (e) { showToast(e.message); } finally { setBusy(false); }
  };
  const removeHoliday = async (hid) => {
    try { await api.removeHoliday(id, hid); await load(); } catch (e) { showToast(e.message); }
  };
  const setWeekend = async (weekendDays) => {
    setCal((c) => ({ ...c, weekendDays }));
    try { await api.updateHolidayCalendar(id, { weekendDays }); } catch (e) { showToast(e.message); load(); }
  };

  return (
    <Drawer anchor="right" open={!!id} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: AG.text }}>{cal ? cal.name : 'Calendar'}</span>
          <IconButton size="small" onClick={onClose} aria-label="Close"><X size={18} /></IconButton>
        </div>
        {!cal ? <CenterSpinner /> : (
          <>
            <div style={{ fontSize: 12, color: AG.n500, marginBottom: 14 }}>{cal.year} · {cal.holidays.length} holiday{cal.holidays.length === 1 ? '' : 's'}</div>
            <div style={{ marginBottom: 16 }}><Field label="Weekend (excluded from leave counting)"><WeekendPicker value={cal.weekendDays} onChange={setWeekend} /></Field></div>
            <div className="flex items-end gap-2" style={{ marginBottom: 14 }}>
              <div style={{ flex: '0 0 140px' }}><Field label="Date"><TextField fullWidth size="small" type="date" value={add.date} onChange={(e) => setAdd({ ...add, date: e.target.value })} /></Field></div>
              <div style={{ flex: 1 }}><Field label="Name"><TextField fullWidth size="small" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} placeholder="Independence Day" /></Field></div>
            </div>
            <PrimaryBtn onClick={addHoliday} disabled={busy}><Plus size={14} style={{ marginRight: 6 }} /> Add holiday</PrimaryBtn>

            <div style={{ marginTop: 18, display: 'grid', gap: 8, overflowY: 'auto' }}>
              {cal.holidays.length === 0 ? (
                <span style={{ fontSize: 13, color: AG.n500 }}>No holidays yet.</span>
              ) : cal.holidays.map((h) => (
                <div key={h.id} className="flex items-center justify-between" style={{ padding: '8px 12px', border: `1px solid ${AG.n200}`, borderRadius: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: AG.text }}>{h.name}</div>
                    <div style={{ fontSize: 12, color: AG.n500 }}>{h.date}</div>
                  </div>
                  <IconButton size="small" onClick={() => removeHoliday(h.id)} aria-label="Remove"><Trash2 size={14} /></IconButton>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}

// ── Overtime rules ───────────────────────────────────────────────────────────
const BLANK_OT = { entityId: '', standardHours: 8, workingDays: 22, weekdayMultiplier: 1.5, weekendMultiplier: 2, holidayMultiplier: 2, active: true };

function OvertimeRules({ showToast, scopeOptions, scopeLabel }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.overtimeRules().then(setRows).catch((e) => { showToast(e.message); setRows([]); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (busy) return; setBusy(true);
    try {
      const p = { standardHours: Number(form.standardHours), workingDays: Number(form.workingDays), weekdayMultiplier: Number(form.weekdayMultiplier), weekendMultiplier: Number(form.weekendMultiplier), holidayMultiplier: Number(form.holidayMultiplier), active: form.active };
      if (form.id) await api.updateOvertimeRule(form.id, p);
      else await api.createOvertimeRule({ ...p, entityId: form.entityId || null });
      showToast('Overtime rule saved'); setForm(null); await load();
    } catch (e) { showToast(e.message); } finally { setBusy(false); }
  };
  const remove = async (r) => { try { await api.deleteOvertimeRule(r.id); await load(); } catch (e) { showToast(e.message); } };

  if (!rows) return <CenterSpinner />;
  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: AG.n500 }}>Overtime policy per OpCo — hours beyond the standard day, multiplied by weekday / weekend / holiday tiers</span>
        <PrimaryBtn onClick={() => setForm({ ...BLANK_OT })}><Plus size={14} style={{ marginRight: 6 }} /> New rule</PrimaryBtn>
      </div>
      {rows.length === 0 ? (
        <Card><EmptyState icon={Timer} title="No overtime rules" desc="A company-wide default applies until you add one." /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {rows.map((r) => (
            <div key={r.id} style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 14, padding: 16, opacity: r.active ? 1 : 0.6 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: AG.text }}>{scopeLabel(r.entityId, r.entityName)}</div>
                <div className="flex items-center gap-1">
                  <IconButton size="small" onClick={() => setForm({ id: r.id, ...r })} aria-label="Edit"><Pencil size={14} /></IconButton>
                  <IconButton size="small" onClick={() => remove(r)} aria-label="Delete"><Trash2 size={14} /></IconButton>
                </div>
              </div>
              <div style={{ fontSize: 12, color: AG.n500, margin: '4px 0 10px' }}>Standard {r.standardHours}h/day · {r.workingDays} days/mo</div>
              <div className="flex items-center" style={{ gap: 6, flexWrap: 'wrap' }}>
                <Pill tone="info">Weekday ×{r.weekdayMultiplier}</Pill>
                <Pill tone="info">Weekend ×{r.weekendMultiplier}</Pill>
                <Pill tone="info">Holiday ×{r.holidayMultiplier}</Pill>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit overtime rule' : 'New overtime rule'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              {!form.id && <Field label="Scope"><Select value={form.entityId} onChange={(v) => setForm({ ...form, entityId: v })} options={scopeOptions} /></Field>}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Standard hours/day"><TextField fullWidth size="small" type="number" value={form.standardHours} onChange={(e) => setForm({ ...form, standardHours: e.target.value })} /></Field>
                <Field label="Working days/month"><TextField fullWidth size="small" type="number" value={form.workingDays} onChange={(e) => setForm({ ...form, workingDays: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Weekday ×"><TextField fullWidth size="small" type="number" inputProps={{ step: 0.1 }} value={form.weekdayMultiplier} onChange={(e) => setForm({ ...form, weekdayMultiplier: e.target.value })} /></Field>
                <Field label="Weekend ×"><TextField fullWidth size="small" type="number" inputProps={{ step: 0.1 }} value={form.weekendMultiplier} onChange={(e) => setForm({ ...form, weekendMultiplier: e.target.value })} /></Field>
                <Field label="Holiday ×"><TextField fullWidth size="small" type="number" inputProps={{ step: 0.1 }} value={form.holidayMultiplier} onChange={(e) => setForm({ ...form, holidayMultiplier: e.target.value })} /></Field>
              </div>
              <FormControlLabel control={<Checkbox checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label="Active" />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={save} disabled={busy}>Save</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Payroll components ───────────────────────────────────────────────────────
const BLANK_COMPONENT = { entityId: '', code: '', name: '', kind: 'Statutory', side: 'Employer', basis: 'PercentOfGross', ratePct: 0, flatAmount: 0, note: '' };

function PayrollComponents({ showToast, scopeOptions, scopeLabel }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.payrollComponents().then(setRows).catch((e) => { showToast(e.message); setRows([]); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    const code = (form.code || '').trim();
    const name = (form.name || '').trim();
    if (!code || !name || busy) return;
    setBusy(true);
    try {
      const payload = { name, kind: form.kind, side: form.side, basis: form.basis, note: (form.note || '').trim() || null };
      if (form.basis === 'PercentOfGross') payload.rate = (Number(form.ratePct) || 0) / 100;
      else payload.flatAmount = Number(form.flatAmount) || 0;
      if (form.id) await api.updatePayrollComponent(form.id, payload);
      else await api.createPayrollComponent({ ...payload, code, entityId: form.entityId || null });
      showToast(form.id ? 'Component updated' : `Component "${name}" created`);
      setForm(null);
      await load();
    } catch (e) { showToast(e.message); } finally { setBusy(false); }
  };
  const toggleActive = async (c) => { try { await api.updatePayrollComponent(c.id, { active: !c.active }); await load(); } catch (e) { showToast(e.message); } };
  const remove = async (c) => { if (busy) return; setBusy(true); try { await api.deletePayrollComponent(c.id); showToast(`Deleted ${c.name}`); await load(); } catch (e) { showToast(e.message); } finally { setBusy(false); } };

  if (!rows) return <CenterSpinner />;
  const amountLabel = (c) => (c.basis === 'Flat' ? `Rs ${c.flatAmount} / mo` : `${Math.round((c.rate || 0) * 1000) / 10}% of gross`);

  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: AG.n500 }}>{rows.length} component{rows.length === 1 ? '' : 's'} · auto-applied on every payroll run</span>
        <PrimaryBtn onClick={() => setForm({ ...BLANK_COMPONENT })}><Plus size={14} style={{ marginRight: 6 }} /> New component</PrimaryBtn>
      </div>
      {rows.length === 0 ? (
        <Card><EmptyState icon={Wallet} title="No payroll components" desc="Add statutory or benefit contributions (PF, EOBI, …)." /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {rows.map((c) => (
            <div key={c.id} style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 14, padding: 16, opacity: c.active ? 1 : 0.6 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: AG.text }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{c.code} · {scopeLabel(c.entityId, c.entity?.name)}</div>
                </div>
                <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                  <IconButton size="small" onClick={() => setForm({ id: c.id, code: c.code, name: c.name, kind: c.kind, side: c.side, basis: c.basis, ratePct: Math.round((c.rate || 0) * 1000) / 10, flatAmount: c.flatAmount || 0, note: c.note || '', entityId: c.entityId || '' })} aria-label="Edit"><Pencil size={15} /></IconButton>
                  <IconButton size="small" onClick={() => remove(c)} aria-label="Delete"><Trash2 size={15} /></IconButton>
                </div>
              </div>
              <div className="flex items-center gap-2" style={{ marginTop: 12, flexWrap: 'wrap' }}>
                <Pill tone={c.kind === 'Statutory' ? 'info' : 'primary'}>{c.kind}</Pill>
                <Pill tone="neutral">{c.side}</Pill>
                <Pill tone="success">{amountLabel(c)}</Pill>
              </div>
              {c.note && <p style={{ fontSize: 12, color: AG.n500, marginTop: 10 }}>{c.note}</p>}
              <div className="flex items-center justify-end" style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${AG.divider}` }}>
                <button type="button" onClick={() => toggleActive(c)} style={{ fontSize: 12, fontWeight: 600, color: c.active ? AG.successDark : AG.n500, background: 'none', border: 'none', cursor: 'pointer' }}>
                  {c.active ? 'Active' : 'Inactive'} · toggle
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit component' : 'New payroll component'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <Field label="Name"><TextField fullWidth size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Provident Fund (PF)" /></Field>
              <Field label="Code"><TextField fullWidth size="small" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="PF" disabled={!!form.id} /></Field>
              {!form.id && <Field label="Scope"><Select value={form.entityId} onChange={(v) => setForm({ ...form, entityId: v })} options={scopeOptions} /></Field>}
              <Field label="Kind"><Select value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} options={[{ value: 'Statutory', label: 'Statutory' }, { value: 'Benefit', label: 'Benefit' }]} /></Field>
              <Field label="Borne by"><Select value={form.side} onChange={(v) => setForm({ ...form, side: v })} options={[{ value: 'Employer', label: 'Employer' }, { value: 'Employee', label: 'Employee' }, { value: 'Both', label: 'Both (matched)' }]} /></Field>
              <Field label="Basis"><Select value={form.basis} onChange={(v) => setForm({ ...form, basis: v })} options={[{ value: 'PercentOfGross', label: '% of gross' }, { value: 'Flat', label: 'Flat monthly amount' }]} /></Field>
              {form.basis === 'PercentOfGross'
                ? <Field label="Rate (% of gross)"><TextField fullWidth size="small" type="number" value={form.ratePct} onChange={(e) => setForm({ ...form, ratePct: e.target.value })} /></Field>
                : <Field label="Flat amount (Rs / month)"><TextField fullWidth size="small" type="number" value={form.flatAmount} onChange={(e) => setForm({ ...form, flatAmount: e.target.value })} /></Field>}
              <Field label="Note"><TextField fullWidth size="small" multiline minRows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={save} disabled={busy}>{form?.id ? 'Save' : 'Create'}</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Approval flows (edit the engine's WorkflowDefinition steps) ──────────────
const APPROVER_OPTIONS = [
  { value: 'manager', label: "Employee's manager" },
  { value: 'hr', label: 'HR (any HR user)' },
];

function Workflows({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null); // { key, name, steps:[...], objectionWindowHours }
  const [busy, setBusy] = useState(false);

  const load = () => api.workflows().then(setRows).catch((e) => { showToast(e.message); setRows([]); });
  useEffect(() => { load(); }, []);

  const edit = (w) => setForm({
    key: w.key, name: w.name,
    objectionWindowHours: w.objectionWindowHours || 0,
    steps: (w.steps || []).map((s) => ({ name: s.name, approverType: s.approverType, slaHours: s.slaHours ?? '', escalateToType: s.escalateToType || '', allowBulk: !!s.allowBulk })),
  });

  const setStep = (i, key, val) => setForm((f) => ({ ...f, steps: f.steps.map((s, j) => (j === i ? { ...s, [key]: val } : s)) }));
  const addStep = () => setForm((f) => (f.steps.length >= 6 ? f : { ...f, steps: [...f.steps, { name: '', approverType: 'hr', slaHours: '', escalateToType: '', allowBulk: false }] }));
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }));

  const save = async () => {
    if (busy || !form.steps.length) return;
    setBusy(true);
    try {
      await api.updateWorkflow(form.key, {
        steps: form.steps.map((s, i) => ({ order: i + 1, name: s.name, approverType: s.approverType, slaHours: s.slaHours === '' ? null : Number(s.slaHours), escalateToType: s.escalateToType || null, allowBulk: !!s.allowBulk })),
        objectionWindowHours: Number(form.objectionWindowHours) || 0,
      });
      showToast(`"${form.name}" updated`);
      setForm(null);
      await load();
    } catch (e) { showToast(e.message); } finally { setBusy(false); }
  };

  if (!rows) return <CenterSpinner />;
  const approverLabel = (t) => APPROVER_OPTIONS.find((o) => o.value === t)?.label || t;

  return (
    <>
      <div style={{ fontSize: 13, color: AG.n500, marginBottom: 14 }}>
        Edit who approves each request type and the SLA per step. Changes apply to <b>new</b> requests; in-flight approvals keep their original chain.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {rows.map((w) => (
          <div key={w.key} style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 14, padding: 16, opacity: w.active ? 1 : 0.6 }}>
            <div className="flex items-start justify-between" style={{ gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: AG.text }}>{w.name}</div>
                <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{w.module} · {(w.steps || []).length} step{(w.steps || []).length === 1 ? '' : 's'}{w.objectionWindowHours ? ` · ${w.objectionWindowHours}h objection` : ''}</div>
              </div>
              <IconButton size="small" onClick={() => edit(w)} aria-label="Edit"><Pencil size={15} /></IconButton>
            </div>
            <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
              {(w.steps || []).map((s) => (
                <div key={s.order} className="flex items-center gap-2" style={{ fontSize: 12.5, color: AG.n700 }}>
                  <span style={{ width: 18, height: 18, borderRadius: 99, background: AG.primaryLightest, color: AG.primaryDark, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.order}</span>
                  <span style={{ fontWeight: 600 }}>{s.name}</span>
                  <span style={{ color: AG.n500 }}>· {approverLabel(s.approverType)}{s.slaHours ? ` · ${s.slaHours}h SLA` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form ? `Edit “${form.name}” flow` : 'Edit flow'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 6 }}>Approval steps (top to bottom, max 6)</div>
              <div style={{ display: 'grid', gap: 10 }}>
                {form.steps.map((s, i) => (
                  <div key={i} style={{ border: `1px solid ${AG.n200}`, borderRadius: 10, padding: 10 }}>
                    <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: AG.n500 }}>Step {i + 1}</span>
                      <div style={{ flex: 1 }} />
                      <IconButton size="small" onClick={() => removeStep(i)} aria-label="Remove step"><Trash2 size={14} /></IconButton>
                    </div>
                    <div className="flex gap-2">
                      <div style={{ flex: 2 }}><TextField fullWidth size="small" placeholder="Step name" value={s.name} onChange={(e) => setStep(i, 'name', e.target.value)} /></div>
                      <div style={{ flex: 2 }}><Select value={s.approverType} onChange={(v) => setStep(i, 'approverType', v)} options={APPROVER_OPTIONS} /></div>
                      <div style={{ flex: 1 }}><TextField fullWidth size="small" type="number" placeholder="SLA h" value={s.slaHours} onChange={(e) => setStep(i, 'slaHours', e.target.value)} /></div>
                    </div>
                  </div>
                ))}
              </div>
              <OutlineBtn onClick={addStep} style={{ marginTop: 10 }} disabled={form.steps.length >= 6}><Plus size={14} style={{ marginRight: 6 }} /> Add step</OutlineBtn>
              <div style={{ marginTop: 14 }}>
                <Field label="Post-approval objection window (hours, 0 = none)"><TextField fullWidth size="small" type="number" value={form.objectionWindowHours} onChange={(e) => setForm({ ...form, objectionWindowHours: e.target.value })} /></Field>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={save} disabled={busy}>Save</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Roles & access (grant granular roles to employees) ───────────────────────
function RolesAccess({ showToast, scopeOptions, scopeLabel }) {
  const [grants, setGrants] = useState(null);
  const [roles, setRoles] = useState([]);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(null); // { employeeId, role, entityId }
  const [busy, setBusy] = useState(false);

  const load = () => api.roleGrants().then(setGrants).catch((e) => { showToast(e.message); setGrants([]); });
  useEffect(() => {
    load();
    api.roles().then(setRoles).catch(() => setRoles([]));
    api.employees().then((es) => setPeople(es || [])).catch(() => setPeople([]));
  }, []);

  const grant = async () => {
    if (!form.employeeId || !form.role || busy) return;
    setBusy(true);
    try {
      await api.grantRole({ employeeId: form.employeeId, role: form.role, entityId: form.entityId || null });
      showToast('Role granted');
      setForm(null);
      await load();
    } catch (e) { showToast(e.message); } finally { setBusy(false); }
  };
  const revoke = async (g) => { if (busy) return; setBusy(true); try { await api.revokeRole(g.id); showToast('Role revoked'); await load(); } catch (e) { showToast(e.message); } finally { setBusy(false); } };

  if (!grants) return <CenterSpinner />;
  const roleOptions = roles.map((r) => ({ value: r.role, label: r.role }));
  const permsOf = (role) => roles.find((r) => r.role === role)?.permissions || [];

  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: AG.n500 }}>Grant granular roles on top of HR/Employee — scoped to an entity or all. HR always has full access.</span>
        <PrimaryBtn onClick={() => setForm({ employeeId: '', role: roles[0]?.role || '', entityId: '' })}><Plus size={14} style={{ marginRight: 6 }} /> Grant role</PrimaryBtn>
      </div>

      {/* Role catalogue */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, marginBottom: 18 }}>
        {roles.map((r) => (
          <div key={r.role} style={{ background: AG.n50, border: `1px solid ${AG.n200}`, borderRadius: 12, padding: 12 }}>
            <div className="flex items-center gap-2"><ShieldCheck size={15} style={{ color: AG.primary }} /><span style={{ fontWeight: 700, fontSize: 13.5, color: AG.text }}>{r.role}</span></div>
            <div style={{ fontSize: 12, color: AG.n500, marginTop: 6 }}>{r.permissions.join(', ')}</div>
          </div>
        ))}
      </div>

      {grants.length === 0 ? (
        <Card><EmptyState icon={ShieldCheck} title="No granular grants yet" desc="Everyone uses the default HR / Employee access. Grant a role to give scoped extra access." /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {grants.map((g) => (
            <div key={g.id} className="flex items-center justify-between" style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 12, padding: '12px 14px', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{g.employeeName} <span style={{ color: AG.n400, fontWeight: 400 }}>· {g.dept}</span></div>
                <div className="flex items-center gap-2" style={{ marginTop: 5 }}>
                  <Pill tone="primary">{g.role}</Pill>
                  <span style={{ fontSize: 12, color: AG.n500 }}>{scopeLabel(g.entityId, g.entityName)}</span>
                </div>
              </div>
              <IconButton size="small" onClick={() => revoke(g)} aria-label="Revoke"><Trash2 size={15} /></IconButton>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Grant a role</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <Field label="Employee">
                <Select value={form.employeeId} onChange={(v) => setForm({ ...form, employeeId: v })} options={[{ value: '', label: 'Select an employee…' }, ...people.map((p) => ({ value: p.id, label: `${p.name} · ${p.dept}` }))]} />
              </Field>
              <Field label="Role"><Select value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={roleOptions} /></Field>
              {form.role && <div style={{ fontSize: 12, color: AG.n500, margin: '-6px 0 12px' }}>Grants: {permsOf(form.role).join(', ')}</div>}
              <Field label="Scope"><Select value={form.entityId} onChange={(v) => setForm({ ...form, entityId: v })} options={scopeOptions} /></Field>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={grant} disabled={busy}>Grant</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Income-tax schemes (band editor) ─────────────────────────────────────────
function TaxSchemes({ showToast, scopeOptions, scopeLabel }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null); // { id?, name, country, entityId, bands: [{upTo, ratePct}] }
  const [busy, setBusy] = useState(false);

  const load = () => api.taxSchemes().then(setRows).catch((e) => { showToast(e.message); setRows([]); });
  useEffect(() => { load(); }, []);

  const toForm = (s) => ({ id: s.id, name: s.name, country: s.country || '', entityId: s.entityId || '', bands: (s.bands || []).map((b) => ({ upTo: b.upTo == null ? '' : String(b.upTo), ratePct: Math.round(b.rate * 1000) / 10 })) });
  const blank = () => ({ name: '', country: '', entityId: '', bands: [{ upTo: '', ratePct: 0 }] });

  const setBand = (i, key, val) => setForm((f) => ({ ...f, bands: f.bands.map((b, j) => (j === i ? { ...b, [key]: val } : b)) }));
  const addBand = () => setForm((f) => ({ ...f, bands: [...f.bands, { upTo: '', ratePct: 0 }] }));
  const removeBand = (i) => setForm((f) => ({ ...f, bands: f.bands.filter((_, j) => j !== i) }));

  const save = async () => {
    const name = (form.name || '').trim();
    if (!name || !form.bands.length || busy) return;
    const bands = form.bands.map((b) => ({ upTo: String(b.upTo).trim() === '' ? null : Number(b.upTo), rate: (Number(b.ratePct) || 0) / 100 }));
    setBusy(true);
    try {
      const payload = { name, country: (form.country || '').trim() || null, bands };
      if (form.id) await api.updateTaxScheme(form.id, payload);
      else await api.createTaxScheme({ ...payload, entityId: form.entityId || null });
      showToast(form.id ? 'Tax scheme updated' : `Scheme "${name}" created`);
      setForm(null);
      await load();
    } catch (e) { showToast(e.message); } finally { setBusy(false); }
  };
  const remove = async (s) => { if (busy) return; setBusy(true); try { await api.deleteTaxScheme(s.id); showToast(`Deleted ${s.name}`); await load(); } catch (e) { showToast(e.message); } finally { setBusy(false); } };

  if (!rows) return <CenterSpinner />;

  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: AG.n500 }}>{rows.length} scheme{rows.length === 1 ? '' : 's'} · progressive annual bands drive income-tax deduction</span>
        <PrimaryBtn onClick={() => setForm(blank())}><Plus size={14} style={{ marginRight: 6 }} /> New scheme</PrimaryBtn>
      </div>
      {rows.length === 0 ? (
        <Card><EmptyState icon={Percent} title="No tax schemes" desc="Add a progressive income-tax band set." /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {rows.map((s) => (
            <div key={s.id} style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 14, padding: 16, opacity: s.active ? 1 : 0.6 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: AG.text }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{s.country ? `${s.country} · ` : ''}{scopeLabel(s.entityId, s.entity?.name)}</div>
                </div>
                <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                  <IconButton size="small" onClick={() => setForm(toForm(s))} aria-label="Edit"><Pencil size={15} /></IconButton>
                  <IconButton size="small" onClick={() => remove(s)} aria-label="Delete"><Trash2 size={15} /></IconButton>
                </div>
              </div>
              <div className="flex items-center gap-2" style={{ marginTop: 12 }}>
                <Pill tone="info">{(s.bands || []).length} bands</Pill>
                <Pill tone={s.active ? 'success' : 'warning'}>{s.active ? 'Active' : 'Inactive'}</Pill>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.id ? 'Edit tax scheme' : 'New tax scheme'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div className="flex gap-2">
                <div style={{ flex: 2 }}><Field label="Name"><TextField fullWidth size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pakistan (salaried)" /></Field></div>
                <div style={{ flex: 1 }}><Field label="Country"><TextField fullWidth size="small" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="PK" /></Field></div>
              </div>
              {!form.id && <Field label="Scope"><Select value={form.entityId} onChange={(v) => setForm({ ...form, entityId: v })} options={scopeOptions} /></Field>}
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600, margin: '8px 0 6px' }}>Bands (annual gross) — leave the top band's “up to” empty for the open band.</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {form.bands.map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <TextField size="small" type="number" placeholder="up to (annual)" value={b.upTo} onChange={(e) => setBand(i, 'upTo', e.target.value)} style={{ flex: 2 }} />
                    <TextField size="small" type="number" placeholder="rate %" value={b.ratePct} onChange={(e) => setBand(i, 'ratePct', e.target.value)} style={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => removeBand(i)} aria-label="Remove band"><Trash2 size={14} /></IconButton>
                  </div>
                ))}
              </div>
              <OutlineBtn onClick={addBand} style={{ marginTop: 10 }}><Plus size={14} style={{ marginRight: 6 }} /> Add band</OutlineBtn>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={save} disabled={busy}>{form?.id ? 'Save' : 'Create'}</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Manpower planning (Phase 1.1) — pay grades (salary band per grade) and
// headcount budgets (planned vs live actual headcount, with alerts).
const HC_TONE = { over: 'error', at: 'warning', warning: 'warning', ok: 'success' };
const HC_LABEL = { over: 'Over budget', at: 'At capacity', warning: 'Near limit', ok: 'On track' };
const PAY_CCY = ['PKR', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'INR'];
const money0 = (n, ccy = 'PKR') => `${ccy === 'PKR' ? 'Rs' : ccy} ${Number(n || 0).toLocaleString('en-IN')}`;
const thisYear = () => Number(new Date().getFullYear());

function Manpower({ showToast, scopeOptions }) {
  const [grades, setGrades] = useState(null);
  const [report, setReport] = useState(null);
  const [gForm, setGForm] = useState(null);
  const [bForm, setBForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadGrades = () => api.payGrades().then(setGrades).catch(() => setGrades([]));
  const loadReport = () => api.manpower().then(setReport).catch(() => setReport({ rows: [], alerts: 0 }));
  useEffect(() => { loadGrades(); loadReport(); }, []);

  const saveGrade = async () => {
    if (busy || !gForm.code?.trim() || !gForm.name?.trim()) return; setBusy(true);
    const payload = { ...gForm, entityId: gForm.entityId || null, minSalary: Number(gForm.minSalary) || 0, maxSalary: Number(gForm.maxSalary) || 0 };
    try {
      if (gForm.id) await api.updatePayGrade(gForm.id, payload); else await api.createPayGrade(payload);
      showToast?.('Pay grade saved'); setGForm(null); loadGrades();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const removeGrade = async (g) => { try { await api.deletePayGrade(g.id); loadGrades(); } catch (e) { showToast?.(e.message); } };

  const saveBudget = async () => {
    if (busy || !bForm.dept?.trim()) return; setBusy(true);
    try {
      if (bForm.id) await api.updateHeadcountBudget(bForm.id, { budgeted: Number(bForm.budgeted) || 0 });
      else await api.createHeadcountBudget({ entityId: bForm.entityId || null, dept: bForm.dept.trim(), year: Number(bForm.year), budgeted: Number(bForm.budgeted) || 0 });
      showToast?.('Budget saved'); setBForm(null); loadReport();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const removeBudget = async (b) => { try { await api.deleteHeadcountBudget(b.id); loadReport(); } catch (e) { showToast?.(e.message); } };

  if (!grades || !report) return <CenterSpinner />;
  return (
    <>
      {/* Pay grades */}
      <div className="flex items-center justify-between" style={{ marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <p style={{ fontSize: 13, color: AG.n500, margin: 0, maxWidth: 560 }}>Pay grades define the salary band for each grade level (the code matches the prefix of an employee’s grade, e.g. <b>L4</b>).</p>
        <PrimaryBtn onClick={() => setGForm({ code: '', name: '', currency: 'PKR', minSalary: '', maxSalary: '', entityId: '', order: grades.length })}><Plus size={14} style={{ marginRight: 6 }} /> Add grade</PrimaryBtn>
      </div>
      {grades.length === 0 ? <Card><EmptyState icon={Wallet} title="No pay grades" desc="Add a grade (e.g. L4 · Senior) with a salary band." /></Card> : (
        <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 26 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead><tr style={{ background: AG.n50, color: AG.n600, textAlign: 'left' }}>{['Grade', 'Band (monthly)', 'Scope', 'Status', ''].map((h) => <th key={h} style={{ padding: '10px 14px', fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {grades.map((g) => (
                <tr key={g.id} style={{ borderTop: `1px solid ${AG.divider}` }}>
                  <td style={{ padding: '10px 14px', color: AG.text, fontWeight: 600 }}>{g.code} · {g.name}</td>
                  <td style={{ padding: '10px 14px', color: AG.text }}>{money0(g.minSalary, g.currency)} – {g.maxSalary > 0 ? money0(g.maxSalary, g.currency) : 'open'}</td>
                  <td style={{ padding: '10px 14px' }}><Pill tone="neutral">{g.entityId ? g.entityName || 'Entity' : 'Company-wide'}</Pill></td>
                  <td style={{ padding: '10px 14px' }}><Pill tone={g.active ? 'success' : 'neutral'}>{g.active ? 'Active' : 'Inactive'}</Pill></td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setGForm({ ...g, currency: g.currency || 'PKR', minSalary: String(g.minSalary), maxSalary: String(g.maxSalary), entityId: g.entityId || '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, marginRight: 6 }}><Pencil size={15} /></button>
                    <button onClick={() => removeGrade(g)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Headcount budgets */}
      <div className="flex items-center justify-between" style={{ marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <p style={{ fontSize: 13, color: AG.n500, margin: 0, maxWidth: 560 }}>Headcount budgets set planned headcount per department and year. Actuals are live; a department raises an alert as it nears or exceeds its budget.{report.alerts > 0 ? <b style={{ color: AG.danger || '#c0392b' }}> {report.alerts} alert(s).</b> : ''}</p>
        <PrimaryBtn onClick={() => setBForm({ dept: '', year: thisYear(), budgeted: '', entityId: '' })}><Plus size={14} style={{ marginRight: 6 }} /> Add budget</PrimaryBtn>
      </div>
      {report.rows.length === 0 ? <Card><EmptyState icon={Wallet} title="No headcount budgets" desc="Add a budget for a department and year to track manpower vs plan." /></Card> : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead><tr style={{ background: AG.n50, color: AG.n600, textAlign: 'left' }}>{['Department', 'Year', 'Budgeted', 'Actual', 'Remaining', 'Status', ''].map((h) => <th key={h} style={{ padding: '10px 14px', fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.id} style={{ borderTop: `1px solid ${AG.divider}` }}>
                  <td style={{ padding: '10px 14px', color: AG.text, fontWeight: 600 }}>{r.dept}{r.entityId ? '' : ' · all'}</td>
                  <td style={{ padding: '10px 14px', color: AG.n500 }}>{r.year}</td>
                  <td style={{ padding: '10px 14px', color: AG.text }}>{r.budgeted}</td>
                  <td style={{ padding: '10px 14px', color: AG.text }}>{r.actual}</td>
                  <td style={{ padding: '10px 14px', color: r.remaining < 0 ? (AG.danger || '#c0392b') : AG.text }}>{r.remaining}</td>
                  <td style={{ padding: '10px 14px' }}><Pill tone={HC_TONE[r.status] || 'neutral'}>{HC_LABEL[r.status] || r.status}</Pill></td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setBForm({ id: r.id, dept: r.dept, year: r.year, budgeted: String(r.budgeted), entityId: r.entityId || '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, marginRight: 6 }}><Pencil size={15} /></button>
                    <button onClick={() => removeBudget(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Pay-grade dialog */}
      <Dialog open={!!gForm} onClose={() => !busy && setGForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{gForm?.id ? 'Edit pay grade' : 'Add pay grade'}</DialogTitle>
        <DialogContent>
          {gForm && (
            <div style={{ paddingTop: 6 }}>
              <div className="flex gap-2">
                <div style={{ flex: 1 }}><Field label="Code"><TextField fullWidth size="small" value={gForm.code} onChange={(e) => setGForm({ ...gForm, code: e.target.value })} placeholder="L4" /></Field></div>
                <div style={{ flex: 2 }}><Field label="Name"><TextField fullWidth size="small" value={gForm.name} onChange={(e) => setGForm({ ...gForm, name: e.target.value })} placeholder="Senior" /></Field></div>
              </div>
              <Field label="Currency"><Select value={gForm.currency || 'PKR'} onChange={(v) => setGForm({ ...gForm, currency: v })} options={PAY_CCY.map((c) => ({ value: c, label: c }))} /></Field>
              <div className="flex gap-2">
                <div style={{ flex: 1 }}><Field label="Min salary"><TextField fullWidth size="small" type="number" value={gForm.minSalary} onChange={(e) => setGForm({ ...gForm, minSalary: e.target.value })} inputProps={{ min: 0 }} /></Field></div>
                <div style={{ flex: 1 }}><Field label="Max salary (0 = open)"><TextField fullWidth size="small" type="number" value={gForm.maxSalary} onChange={(e) => setGForm({ ...gForm, maxSalary: e.target.value })} inputProps={{ min: 0 }} /></Field></div>
              </div>
              {!gForm.id && <Field label="Scope"><Select value={gForm.entityId} onChange={(v) => setGForm({ ...gForm, entityId: v })} options={scopeOptions} /></Field>}
              {gForm.id && <FormControlLabel control={<Checkbox checked={gForm.active !== false} onChange={(e) => setGForm({ ...gForm, active: e.target.checked })} />} label="Active" />}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setGForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={saveGrade} disabled={busy || !gForm?.code?.trim() || !gForm?.name?.trim()}>Save</PrimaryBtn>
        </DialogActions>
      </Dialog>

      {/* Headcount-budget dialog */}
      <Dialog open={!!bForm} onClose={() => !busy && setBForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{bForm?.id ? 'Edit budget' : 'Add headcount budget'}</DialogTitle>
        <DialogContent>
          {bForm && (
            <div style={{ paddingTop: 6 }}>
              <Field label="Department"><TextField fullWidth size="small" value={bForm.dept} onChange={(e) => setBForm({ ...bForm, dept: e.target.value })} disabled={!!bForm.id} placeholder="Engineering" /></Field>
              <div className="flex gap-2">
                <div style={{ flex: 1 }}><Field label="Year"><TextField fullWidth size="small" type="number" value={bForm.year} onChange={(e) => setBForm({ ...bForm, year: e.target.value })} disabled={!!bForm.id} inputProps={{ min: 2000, max: 2100 }} /></Field></div>
                <div style={{ flex: 1 }}><Field label="Budgeted headcount"><TextField fullWidth size="small" type="number" value={bForm.budgeted} onChange={(e) => setBForm({ ...bForm, budgeted: e.target.value })} inputProps={{ min: 0 }} /></Field></div>
              </div>
              {!bForm.id && <Field label="Scope"><Select value={bForm.entityId} onChange={(v) => setBForm({ ...bForm, entityId: v })} options={scopeOptions} /></Field>}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setBForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={saveBudget} disabled={busy || !bForm?.dept?.trim()}>Save</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Exchange rates (Phase 2 multi-currency) — HR maintains FX rates used for
// consolidated payroll reporting. Conversion is display-only; payroll math stays
// in each employee's native currency.
const FX_CODES = ['USD', 'PKR', 'EUR', 'GBP', 'AED', 'SAR', 'INR'];
function ExchangeRates({ showToast, scopeOptions }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.exchangeRates().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (busy) return; setBusy(true);
    try { await api.addExchangeRate({ ...form, entityId: form.entityId || null }); showToast?.('Rate saved'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const remove = async (r) => { try { await api.deleteExchangeRate(r.id); load(); } catch (e) { showToast?.(e.message); } };

  if (!rows) return <CenterSpinner />;
  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <p style={{ fontSize: 13, color: AG.n500, margin: 0, maxWidth: 560 }}>FX rates power the “View in …” currency toggle on Payroll. Display only — payroll is always calculated in each employee’s native currency.</p>
        <PrimaryBtn onClick={() => setForm({ fromCurrency: 'PKR', toCurrency: 'USD', rate: '', effectiveDate: new Date().toISOString().slice(0, 10), entityId: '' })}><Plus size={14} style={{ marginRight: 6 }} /> Add rate</PrimaryBtn>
      </div>
      {rows.length === 0 ? <Card><EmptyState icon={Wallet} title="No exchange rates" desc="Add a rate (e.g. PKR → USD) to enable consolidated payroll reporting." /></Card> : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead><tr style={{ background: AG.n50, color: AG.n600, textAlign: 'left' }}>{['Pair', 'Rate', 'Effective', 'Scope', ''].map((h) => <th key={h} style={{ padding: '10px 14px', fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: `1px solid ${AG.divider}` }}>
                  <td style={{ padding: '10px 14px', color: AG.text, fontWeight: 600 }}>{r.fromCurrency} → {r.toCurrency}</td>
                  <td style={{ padding: '10px 14px', color: AG.text }}>{r.rate}</td>
                  <td style={{ padding: '10px 14px', color: AG.n500 }}>{r.effectiveDate}</td>
                  <td style={{ padding: '10px 14px' }}><Pill tone="neutral">{r.entityId ? r.entityName || 'Entity' : 'Company-wide'}</Pill></td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}><button onClick={() => remove(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add exchange rate</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div className="flex gap-2">
                <div style={{ flex: 1 }}><Field label="From"><Select value={form.fromCurrency} onChange={(v) => setForm({ ...form, fromCurrency: v })} options={FX_CODES.map((c) => ({ value: c, label: c }))} /></Field></div>
                <div style={{ flex: 1 }}><Field label="To"><Select value={form.toCurrency} onChange={(v) => setForm({ ...form, toCurrency: v })} options={FX_CODES.map((c) => ({ value: c, label: c }))} /></Field></div>
              </div>
              <Field label={`Rate (1 ${form.fromCurrency} = ? ${form.toCurrency})`}><TextField fullWidth size="small" type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} inputProps={{ step: 'any', min: 0 }} /></Field>
              <Field label="Effective date"><TextField fullWidth size="small" type="date" value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} InputLabelProps={{ shrink: true }} /></Field>
              <Field label="Scope"><Select value={form.entityId} onChange={(v) => setForm({ ...form, entityId: v })} options={scopeOptions} /></Field>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={create} disabled={busy || !form?.rate}>Create</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Integrations (Phase 5) — per-channel master toggle + provider selection +
// observability. Credentials live in env; this is the configurable layer over the
// email / SMS / e-sign / calendar seams.
const CHANNEL_LABELS = { Email: 'Email (notifications, invites)', SMS: 'SMS', Esign: 'E-signature', Calendar: 'Calendar' };
function Integrations({ showToast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => api.integrations().then(setData).catch(() => setData({ providers: {}, status: [] }));
  useEffect(() => { load(); }, []);

  const save = async (ch, patch) => {
    setBusy(ch.type);
    try { await api.updateIntegration(ch.type, { provider: ch.provider, enabled: ch.enabled, ...patch }); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(''); }
  };

  if (!data) return <CenterSpinner />;
  const providers = data.providers || {};
  return (
    <>
      <p style={{ fontSize: 13, color: AG.n500, margin: '0 0 14px', maxWidth: 640 }}>Turn delivery channels on or off and pick a provider. These settings are <b>company-wide</b> (one shared provider account across all entities) and can only be changed from the consolidated “All entities” view. Credentials are supplied via environment configuration (never stored here) — a channel shows <b>Live</b> only when it’s both credentialed and switched on.</p>
      <div style={{ display: 'grid', gap: 10 }}>
        {(data.status || []).map((ch) => (
          <Card key={ch.type} style={{ padding: 16 }}>
            <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600, color: AG.text }}>
                  {CHANNEL_LABELS[ch.type] || ch.type}{' '}
                  {ch.effective ? <Pill tone="success">Live</Pill> : ch.enabled ? <Pill tone="warning">Not configured</Pill> : <Pill tone="neutral">Off</Pill>}
                </div>
                <div style={{ fontSize: 12, color: AG.n500, marginTop: 3 }}>
                  Credentials: {ch.configured ? 'present (env)' : 'not set'} · Toggle: {ch.enabled ? 'on' : 'off'}{ch.provider ? ` · ${ch.provider}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(providers[ch.type] || []).length > 0 && (
                  <select className="ag-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }} value={ch.provider || ''} disabled={busy === ch.type} onChange={(e) => save(ch, { provider: e.target.value || null })}>
                    <option value="">Provider…</option>
                    {(providers[ch.type] || []).map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                )}
                <OutlineBtn onClick={() => save(ch, { enabled: !ch.enabled })} disabled={busy === ch.type}>{ch.enabled ? 'Turn off' : 'Turn on'}</OutlineBtn>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
